import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getProsConsoleMetrics, getProsConsoleSummary, type ProsConsoleMetrics, type ProsConsoleSummary } from '../../api/prosConsole'

type RangeOption = '7' | '30' | '90' | 'all'

const RANGE_OPTIONS: Array<{ key: RangeOption; label: string }> = [
  { key: '7', label: '7d' },
  { key: '30', label: '30d' },
  { key: '90', label: '90d' },
  { key: 'all', label: 'All' },
]

function parseRange(raw: string | null): RangeOption {
  if (raw === '7' || raw === '30' || raw === '90' || raw === 'all') return raw
  return '30'
}

function chartMax(items: number[]) {
  const max = Math.max(...items, 0)
  return max > 0 ? max : 1
}

function activityIcon(type: string) {
  if (type === 'request_received') return '📥'
  if (type === 'quote_sent') return '💬'
  if (type === 'escrow_funded') return '🔒'
  if (type === 'delivered') return '📦'
  if (type === 'approved') return '✅'
  if (type === 'escrow_released') return '💸'
  if (type === 'dispute_opened') return '⚠'
  if (type === 'refund_issued') return '↩'
  return '•'
}

function toDayHeading(value: string | null) {
  if (!value) return 'Unknown'
  const date = new Date(value)
  const today = new Date()
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (isSameDay(date, today)) return 'Today'
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (isSameDay(date, yesterday)) return 'Yesterday'
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function groupActivity(items: ProsConsoleMetrics['recent_activity']) {
  const groups = new Map<string, ProsConsoleMetrics['recent_activity']>()
  items.forEach((item) => {
    const key = toDayHeading(item.at)
    const current = groups.get(key) || []
    current.push(item)
    groups.set(key, current)
  })
  return Array.from(groups.entries())
}

function MetricSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="h-5 w-40 animate-pulse rounded bg-white/10" />
        <div className="h-8 w-52 animate-pulse rounded-lg bg-white/10" />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="h-24 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="h-72 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
        <div className="h-72 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
      </div>
    </div>
  )
}

export default function ProsOverviewTab() {
  const [searchParams, setSearchParams] = useSearchParams()
  const range = parseRange(searchParams.get('range'))
  const [data, setData] = useState<ProsConsoleMetrics | null>(null)
  const [summary, setSummary] = useState<ProsConsoleSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const setTab = (tab: string, extra?: Record<string, string>) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    Object.entries(extra || {}).forEach(([key, value]) => next.set(key, value))
    setSearchParams(next)
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const daysParam: number | 'all' = range === 'all' ? 'all' : Number(range)
    Promise.all([getProsConsoleMetrics(daysParam), getProsConsoleSummary()])
      .then(([metricsPayload, summaryPayload]) => {
        if (cancelled) return
        setData(metricsPayload)
        setSummary(summaryPayload)
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load dashboard metrics.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [range, reloadKey])

  const earnedSeries = data?.series.earned_by_day_pixe || []
  const bookingsSeries = data?.series.bookings_by_day || []
  const earnedValues = useMemo(() => earnedSeries.map((row) => Number(row.pixe || 0)), [earnedSeries])
  const bookingValues = useMemo(() => bookingsSeries.map((row) => Number(row.count || 0)), [bookingsSeries])
  const earnedMax = useMemo(() => chartMax(earnedValues), [earnedValues])
  const bookingsMax = useMemo(() => chartMax(bookingValues), [bookingValues])
  const hasReleasedEarnings = useMemo(() => earnedValues.some((value) => value > 0), [earnedValues])
  const hasBookings = useMemo(() => bookingValues.some((value) => value > 0), [bookingValues])
  const groupedActivity = useMemo(() => groupActivity(data?.recent_activity || []), [data])

  const setupItems = useMemo(() => {
    const items: Array<{ label: string; action: () => void }> = []
    if ((summary?.counts.skills || 0) < 1) items.push({ label: 'Add at least one skill', action: () => setTab('profile') })
    if ((summary?.counts.services || 0) < 1) items.push({ label: 'Add your first service', action: () => setTab('services') })
    if ((summary?.counts.weekly_ranges || 0) < 1) items.push({ label: 'Set weekly booking hours', action: () => setTab('booking') })
    if (summary?.stripe?.payouts_connect_status !== 'enabled') items.push({ label: 'Connect Stripe for withdrawals', action: () => setTab('payouts') })
    return items.slice(0, 5)
  }, [summary, searchParams])

  if (loading) return <MetricSkeleton />
  if (error) return <div className="text-sm text-red-300">{error}</div>
  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-white">Overview</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-1">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.set('tab', 'overview')
                  next.set('range', option.key)
                  setSearchParams(next)
                }}
                className={`rounded-md px-2 py-1 text-xs ${range === option.key ? 'bg-brand/25 text-white' : 'text-white/70 hover:bg-white/10'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setReloadKey((prev) => prev + 1)}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/85 hover:bg-white/10"
          >
            Refresh
          </button>
        </div>
      </div>

      {setupItems.length > 0 ? (
        <div className="rounded-xl border border-amber-300/20 bg-amber-500/10 p-3">
          <div className="text-sm font-semibold text-amber-100">Setup checklist</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {setupItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.action}
                className="rounded-lg border border-amber-200/20 bg-black/20 px-3 py-1.5 text-xs text-amber-100/90 hover:bg-black/35"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Link to="/jobs?escrow_status=HELD" className="rounded-xl border border-white/10 bg-white/5 p-4 text-left hover:bg-white/10">
          <div className="text-xs text-white/60">Held in escrow (PIXE)</div>
          <div className="mt-1 text-xl font-bold text-white">{data.kpis.held_pixe || '0'}</div>
          <div className="mt-1 text-xs text-white/55">Escrow currently held</div>
        </Link>
        <button type="button" onClick={() => setTab('earnings', { type: 'escrow_released' })} className="rounded-xl border border-white/10 bg-white/5 p-4 text-left hover:bg-white/10">
          <div className="text-xs text-white/60">Earned (released, {range === 'all' ? 'all-time' : `${range}d`})</div>
          <div className="mt-1 text-xl font-bold text-white">{data.kpis.earned_pixe || '0'}</div>
          <div className="mt-1 text-xs text-white/55">Released after approval</div>
        </button>
        <button type="button" onClick={() => setTab('payouts')} className="rounded-xl border border-white/10 bg-white/5 p-4 text-left hover:bg-white/10">
          <div className="text-xs text-white/60">Withdrawable (PIXE)</div>
          <div className="mt-1 text-xl font-bold text-white">{data.kpis.withdrawable_pixe || '-'}</div>
          <div className="mt-1 text-xs text-white/55">Available for withdrawal gate</div>
        </button>
        <button type="button" onClick={() => setTab('requests')} className="rounded-xl border border-white/10 bg-white/5 p-4 text-left hover:bg-white/10">
          <div className="text-xs text-white/60">Pending requests</div>
          <div className="mt-1 text-xl font-bold text-white">{data.kpis.pending_requests}</div>
          <div className="mt-1 text-xs text-white/55">Awaiting your action</div>
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-xs text-white/60">Completed jobs ({range === 'all' ? 'all-time' : `${range}d`})</div>
        <div className="mt-1 text-xl font-bold text-white">{data.kpis.completed_jobs}</div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="mb-2 text-sm font-semibold text-white">Earnings released by day (PIXE)</div>
          {!hasReleasedEarnings ? (
            <div className="rounded-lg border border-white/10 bg-black/25 p-3 text-xs text-white/70">
              No released earnings yet. Fund escrow, deliver work, and get client approval to release earnings.
            </div>
          ) : (
            <div className="space-y-1">
              {earnedSeries.map((row) => {
                const value = Number(row.pixe || 0)
                const pct = (value / earnedMax) * 100
                return (
                  <div key={row.date} className="flex items-center gap-2 text-xs">
                    <div className="w-20 shrink-0 text-white/55">{row.date.slice(5)}</div>
                    <div className="h-2 flex-1 overflow-hidden rounded bg-white/10">
                      <div className="h-full rounded bg-emerald-400/70" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-20 text-right text-white/75">{row.pixe} PIXE</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="mb-2 text-sm font-semibold text-white">Bookings by day</div>
          {!hasBookings ? (
            <div className="rounded-lg border border-white/10 bg-black/25 p-3 text-xs text-white/70">
              Bookings not enabled yet.
              <button type="button" onClick={() => setTab('booking')} className="ml-2 rounded border border-white/15 px-2 py-0.5 text-white/85 hover:bg-white/10">
                Configure booking
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {bookingsSeries.map((row) => {
                const pct = (Number(row.count || 0) / bookingsMax) * 100
                return (
                  <div key={row.date} className="flex items-center gap-2 text-xs">
                    <div className="w-20 shrink-0 text-white/55">{row.date.slice(5)}</div>
                    <div className="h-2 flex-1 overflow-hidden rounded bg-white/10">
                      <div className="h-full rounded bg-sky-400/70" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-8 text-right text-white/75">{row.count}</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="mb-3 text-sm font-semibold text-white">Recent activity</div>
        {groupedActivity.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-black/25 p-3 text-sm text-white/70">
            No activity yet. Share your Pro card to start receiving requests.
            <Link to="/pros/discover" className="ml-2 rounded border border-white/15 px-2 py-1 text-xs text-white hover:bg-white/10">
              Browse Pros
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {groupedActivity.map(([heading, items]) => (
              <div key={heading} className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-white/55">{heading}</div>
                {items.map((row, idx) => (
                  <div key={`${row.type}-${row.at}-${idx}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="truncate text-white/90">
                        <span className="mr-1.5">{activityIcon(row.type)}</span>
                        {row.title}
                      </div>
                      <div className="truncate text-xs text-white/60">
                        {row.subtitle || (row.at ? new Date(row.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {row.amount_pixe ? <div className="text-xs text-emerald-200">{row.amount_pixe} PIXE</div> : null}
                      {row.request_id ? (
                        <Link to={`/pros/requests/${row.request_id}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                          Open request
                        </Link>
                      ) : row.job_id ? (
                        <Link to={`/jobs/${row.job_id}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                          Open job
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
